import { logger } from "@sentry/react";
import { LanguageCode } from "@visikon/core-models/content";
import axios, { AxiosError } from "axios";
import { APIUtils } from "commons/utils";
import { V2API } from "config";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useRecoilState } from "recoil";
import { tokenState } from "redux-recoil-sync.state";
import { URLS } from "./urls";
import { XMLParser } from "fast-xml-parser";
import { LexemeCreationEntry, LexemeEntry, LexiconXML } from "@visikon/core-models/phonetics";

export const PHONETICS_LIST_CACHE_KEY = "phoneticsList";
export const PHONETICS_FILE_URL = (lang: string) => `${V2API.baseBlobUrl}azure_phonetics_${lang}.xml`;

export function usePhoneticsList(lang: LanguageCode) {
  return useQuery([PHONETICS_LIST_CACHE_KEY, lang], () => getAllLexemes(lang), {
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    onError: (error) => logger.error('Failed to fetch phonetics list', { error }),
  });
}

async function getAllLexemes(lang: string): Promise<LexemeEntry[]> {
  const xmlDoc = await fetchAzurePhoneticsConfig(lang);

  // Get all lexemes


  // Extract fields from each lexeme and return as an array of LexemeEntry
  return xmlDoc?.lexicon.lexeme ?? [];
}


async function fetchAzurePhoneticsConfig(lang: string) {
  const url = PHONETICS_FILE_URL(lang);
  try {
    const xmlData = await fetch(url, {
      cache: 'no-cache', // Allow caching, but revalidate on each request
    })
    const xmlDataText = await xmlData.text();
    const parser = new XMLParser({
      ignoreAttributes: false,
      // Do not try to parse values as a numeric value, which causes issues with number lexeme entries (eg. 1 og 9)
      // We don't have any numeric values in the lexicon
      parseTagValue: false,
      isArray(tagName) {
        return tagName === 'lexeme';
      },
    });
    const parsed = parser.parse(xmlDataText) as LexiconXML;
    return parsed;
  } catch (err) {
    logger.error(err);
    return null;
  }
}

interface CoreValidationError {
  message: string;
}

type LexiconUpdateRequest = {
  language: LanguageCode,
  // The lexeme object contains all necessary fields for both creating and updating a lexeme. If an id is present, it will be treated as an update; if not, it will be treated as a creation.
  lexeme: LexemeCreationEntry & { primary?: boolean },
};

export function useUpsertLexeme(onSuccess?: (lexeme: LexemeEntry) => void) {
  const [token] = useRecoilState(tokenState);
  const queryClient = useQueryClient();

  return useMutation<LexemeEntry, AxiosError<CoreValidationError>, LexiconUpdateRequest>({
    mutationFn: async ({ lexeme, language }) => {
      const res = await axios.put(URLS.phonetics(language).upsert(), lexeme, APIUtils.tokenHeader(token!));

      return res.data;
    },
    onSuccess(lexeme, variables) {
      queryClient.invalidateQueries([PHONETICS_LIST_CACHE_KEY, variables.language]);
      onSuccess?.(lexeme);
    },
  });
}

export function useDeleteLexeme() {
  const [token] = useRecoilState(tokenState);
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<CoreValidationError>, { id: string; language: LanguageCode }>({
    mutationFn: async ({ id, language }) => {
      await axios.delete(URLS.phonetics(language).delete(id), APIUtils.tokenHeader(token!));
    },
    onSuccess(_, variables) {
      queryClient.setQueriesData([PHONETICS_LIST_CACHE_KEY, variables.language], (oldData: LexemeEntry[] | undefined) => oldData ? oldData.filter((lexeme) => lexeme.id !== variables.id) : []);
      queryClient.invalidateQueries([PHONETICS_LIST_CACHE_KEY, variables.language]);
    },
  })
}
